import { lazy, Suspense } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { langPrefix } from '@/i18n';
import HomeHeader from '@/components/home/HomeHeader';
import HeroSection from '@/components/home/HeroSection';
import StudyPlanResumeCard from '@/components/home/StudyPlanResumeCard';
import HomeFooter from '@/components/home/HomeFooter';
import SEOHead from '@/components/SEOHead';
import LazyOnVisible from '@/components/LazyOnVisible';

const TestRoundsSection = lazy(() => import('@/components/home/TestRoundsSection'));
const RecommendationTabs = lazy(() => import('@/components/RecommendationTabs'));
const StudyWidgets = lazy(() => import('@/components/StudyWidgets'));
const QuestionPoolSection = lazy(() => import('@/components/home/QuestionPoolSection'));
const Leaderboard = lazy(() => import('@/components/Leaderboard'));
const QuickLinksAndGrammar = lazy(() => import('@/components/home/QuickLinksAndGrammar'));
const CrossLinkCards = lazy(() => import('@/components/CrossLinkCards'));
const ScorePredictor = lazy(() => import('@/components/home/ScorePredictor'));
const TrustBadges = lazy(() => import('@/components/home/TrustBadges'));
const PriceComparisonBanner = lazy(() => import('@/components/home/PriceComparisonBanner'));
const CredentialsBlock = lazy(() => import('@/components/home/CredentialsBlock'));
const HomeFAQ = lazy(() => import('@/components/home/HomeFAQ'));
const HomeEditorialBlock = lazy(() => import('@/components/home/HomeEditorialBlock'));

const Index = () => {
  const { lang, t } = useI18n();

  const path = langPrefix(lang) || '/';

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={t.siteTitle}
        description={t.siteDescription}
        path={path}
        ogImage="/og-home.jpg"
      />
      {/* JSON-LD is injected by SEOHead — no duplicate here */}
      <HomeHeader />
      <main id="main-content" role="main">
      <HeroSection />
      <StudyPlanResumeCard />
      <Suspense fallback={<div className="h-[88px]" />}>
        <PriceComparisonBanner />
      </Suspense>
      {/*
        Below-the-fold sections are mounted only when they near the viewport.
        Each LazyOnVisible reserves vertical space via min-height to keep CLS≈0,
        while deferring Supabase fetches (Leaderboard, QuestionPoolSection) and
        heavy renders off the mobile critical path.
      */}
      <Suspense fallback={null}>
        {/* ScorePredictor moved up: it's our strongest lead-magnet — get visitors interacting in <30s */}
        <LazyOnVisible minHeight={460}>
          <ScorePredictor />
        </LazyOnVisible>
        <LazyOnVisible minHeight={140}>
          <StudyWidgets />
        </LazyOnVisible>
        <LazyOnVisible minHeight={480}>
          <TestRoundsSection />
        </LazyOnVisible>
        <LazyOnVisible minHeight={360}>
          <RecommendationTabs />
        </LazyOnVisible>
        <LazyOnVisible minHeight={180}>
          <TrustBadges />
        </LazyOnVisible>
        <LazyOnVisible minHeight={360}>
          <CredentialsBlock />
        </LazyOnVisible>
        <LazyOnVisible minHeight={520}>
          <QuestionPoolSection />
        </LazyOnVisible>
        <LazyOnVisible minHeight={520}>
          <Leaderboard />
        </LazyOnVisible>
        <LazyOnVisible minHeight={720}>
          <QuickLinksAndGrammar />
        </LazyOnVisible>
        <div className="border-t border-border" />
        <LazyOnVisible minHeight={320}>
          <CrossLinkCards />
        </LazyOnVisible>
        <LazyOnVisible minHeight={480}>
          <HomeEditorialBlock />
        </LazyOnVisible>
        <LazyOnVisible minHeight={520}>
          <HomeFAQ />
        </LazyOnVisible>
      </Suspense>
      </main>
      <HomeFooter />
    </div>
  );
};

export default Index;
